<template>
  <form
    :id="formId"
    method="post"
    class="form-horizontal"
    autocomplete="off"
    @submit.prevent
  >
    <h6 class="sub-section-title">
      {{ $t('registration.whoWillBeRiding') }}
    </h6>
    <div class="row">
      <label class="col-sm-3 control-label mt-10"> {{ $t('customerInfo.name') }}* </label>
      <div class="col-sm-2">
        <sanitized-input
          id="prefix"
          v-model="customerInfo.prefix"
        />
        <span class="help-block">
          {{ $t('customerInfo.prefix') }}
        </span>
      </div>
      <div class="col-sm-3 m-pt-10">
        <sanitized-input
          id="firstName"
          v-model="customerInfo.firstName"
          required
        />
        <span class="help-block"> {{ $t('customerInfo.firstName') }}* </span>
      </div>
      <div class="col-sm-3 m-pt-10">
        <sanitized-input
          id="lastName"
          v-model="customerInfo.lastName"
          required
        />
        <span class="help-block"> {{ $t('customerInfo.lastName') }}* </span>
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label">
        {{ $t('customerInfo.birthday') }}
      </label>
      <div class="col-sm-3">
        <date-picker
          v-model="customerInfo.dob"
          :editable="true"
          placeholder="YYYY/MM/DD"
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label">
        {{ $t('customerInfo.primaryLanguage') }}
      </label>
      <div class="col-sm-3">
        <dropdown-list
          v-model="customerInfo.language1"
          :options="languageList"
        />
      </div>

      <label class="col-sm-2 control-label m-pt-10">
        {{ $t('customerInfo.secondaryLanguage') }}
      </label>
      <div class="col-sm-3">
        <dropdown-list
          v-model="customerInfo.language2"
          :options="languageList"
        />
      </div>
    </div>

    <h6 class="sub-section-title m-mt-30">
      {{ $t('customerInfo.contactInfo') }}
    </h6>
    <div class="row">
      <label class="col-sm-3 control-label mt-10">
        {{ $t('customerInfo.phoneNumber') }}
      </label>
      <div class="col-sm-4">
        <phone-number-input v-model="customerInfo.phoneNumber" />
        <span class="help-block"> {{ $t('customerInfo.mobile') }}* </span>
      </div>
      <div class="col-sm-4 m-pt-10">
        <phone-number-input
          v-model="customerInfo.homeNumber"
          :required="true"
        />
        <span class="help-block">
          {{ $t('customerInfo.home') }}
        </span>
      </div>
    </div>

    <div class="row">
      <label class="col-sm-3 control-label pt-10">
        {{ $t('customerInfo.email') }}
        ({{ $t('customerInfo.forReceipts') }})
      </label>
      <div class="col-sm-4">
        <email-input
          v-model="customerInfo.email"
          placeholder="Recommended"
          @update-validation="updateEmailValidation"
        />
      </div>
    </div>

    <h6 class="sub-section-title m-mt-30">
      {{ $t('customerInfo.homeAddress') }}
    </h6>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label"> {{ $t('address.streetAddress') }}* </label>
      <div class="col-sm-4">
        <address-autocomplete-input
          v-model="customerInfo.streetAddress"
          :anchor-bound="anchorBound"
          @place-changed="onStreetAddressUpdateFromGoogle"
        />
      </div>

      <label class="col-sm-1 control-label m-pt-10">
        {{ $t('address.streetAddress2') }}
      </label>

      <div class="col-sm-2">
        <sanitized-input
          v-model="customerInfo.streetAddress2"
          placeholder="Apt and/or building number"
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label"> {{ $t('address.city') }}* </label>
      <div class="col-sm-4">
        <sanitized-input
          v-model="customerInfo.city"
          required
          placeholder="Independence"
        />
      </div>
      <label class="col-sm-1 control-label m-pt-10"> {{ $t('address.state') }}* </label>
      <div class="col-sm-3">
        <sanitized-input
          v-model="customerInfo.state"
          required
          placeholder="KS"
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label"> {{ $t('address.zip') }}* </label>
      <div class="col-sm-4">
        <sanitized-input
          v-model="customerInfo.zip"
          required
          placeholder="67301"
        />
      </div>

      <label class="col-sm-1 control-label m-pt-10"> {{ $t('address.country') }}* </label>
      <div class="col-sm-3">
        <dropdown-list
          v-model="customerInfo.country"
          :options="countryList"
          required
        />
      </div>
    </div>

    <div class="row">
      <label class="col-sm-3 control-label mt-10">
        {{ $t('customerInfo.driverHomePickupInstruction') }}
      </label>
      <div class="col-sm-8">
        <sanitized-textarea
          v-model="customerInfo.homeNote"
          placeholder="i.e. 'Please tell the guard you're there for Miranda.'"
        />
      </div>
    </div>

    <h6 class="sub-section-title m-mt-30">
      {{ $t('customerInfo.additionalInformation') }}
    </h6>
    <div class="form-group row">
      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isNeedHelp">
          {{ $t('customerInfo.mayNeedExtraHelp') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isNoBigCars">
          {{ $t('customerInfo.cannotGetIntoBigCars') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isNoSmallCars">
          {{ $t('customerInfo.cannotGetIntoSmallCars') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isVisuallyImpaired">
          {{ $t('customerInfo.visuallyImpaired') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isCane">
          {{ $t('customerInfo.usesCane') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isTraveling">
          Is Traveling
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isElbowAssist">
          {{ $t('customerInfo.isElbowAssist') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isWalker">
          {{ $t('customerInfo.usesWalker') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isWheelchair">
          {{ $t('customerInfo.manualWheelchair') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isPowerWheelchairScooter">
          {{ $t('customerInfo.powerWheelchair') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isHardOfHearing">
          {{ $t('customerInfo.hardOfHearing') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isServiceDog">
          {{ $t('customerInfo.serviceDog') }}
          <span v-show="customerInfo.isServiceDog">&nbsp;&nbsp;🐶</span>
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isOxygenTank">
          {{ $t('customerInfo.portableOxygenTank') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isPoorMemory">
          {{ $t('customerInfo.poorMemory') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isVerballyImpaired">
          {{ $t('customerInfo.verballyImpaired') }}
        </el-checkbox>
      </div>

      <div class="col-sm-3 checkbox-container">
        <el-checkbox v-model="customerInfo.isAda">
          {{ $t('customerInfo.adaProtectedDisability') }}
        </el-checkbox>
      </div>
    </div>

    <div class="row">
      <div class="col text-center">
        <p-button
          data-test="btn-get-customer"
          type="purple"
          native-type="submit"
          round
          :disabled="!isFormValid"
          :pressed="true"
          @click="onFormSubmit"
        >
          {{ $t('registration.next') }}
        </p-button>
      </div>
    </div>
  </form>
</template>

<script lang="ts">
import { ref, computed } from 'vue';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { languageList } from '@/constants/app';
import { countryList } from '@/constants/address';
import { CallerProperty } from '@/models/caller';
import { CustomLocationType } from '@/models/address';

const formId = 'complete-registration-update-customer-info';

export default {
  props: {
    customerInfo: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['submitClicked', 'addressChanged'],

  setup: (props: any, context: any) => {
    const { emit } = context;

    const { anchorBound } = useCustomer();

    const isEmailValid = ref(true);

    const isFormValid = computed(() => {
      const requiredProperties: CallerProperty[] = [
        'lastName',
        'firstName',
        'phoneNumber',
        'streetAddress',
        'city',
        'state',
        'zip',
        'country',
      ];
      for (const property of requiredProperties) {
        if (!props.customerInfo[property]) {
          return false;
        }
      }

      if (!isEmailValid.value) {
        return false;
      }

      const phoneNumbers: CallerProperty[] = ['phoneNumber', 'homeNumber'];
      for (const phoneNumberProperty of phoneNumbers) {
        if (props.customerInfo[phoneNumberProperty] === '1') {
          continue;
        }

        if (props.customerInfo[phoneNumberProperty] && !util.isPhoneNumberValid(props.customerInfo[phoneNumberProperty])) {
          return false;
        }
      }

      return true;
    });

    const updateEmailValidation = (value: boolean) => {
      isEmailValid.value = value;
    };

    const onStreetAddressUpdateFromGoogle = (newAddress: CustomLocationType | null) => emit('addressChanged', newAddress);

    const onFormSubmit = () => emit('submitClicked');

    return {
      formId,
      languageList,
      countryList,
      isFormValid,
      anchorBound,
      isEmailValid,

      onStreetAddressUpdateFromGoogle,
      onFormSubmit,
      updateEmailValidation,
    };
  },
};
</script>

<style scoped>
input:invalid {
  background-color: #fdf2b5;
  border: 1px solid #c7c98b !important;
}
</style>
